import { Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  FBPageInnerTabsContainer,
  FBPageTableContainer,
} from 'components/FbUI/Page/styles'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { NotificationPreferencesTable } from 'features/settings/components/NotificationPreferencesTable'
import { useAuth } from 'context/authentication/useAuth'

type NotificationsTab =
  | 'platform'
  | 'deals'
  | 'contacts'
  | 'campaigns'
  | 'rebates'

const DEFAULT_TAB: NotificationsTab = 'platform'

const ALL_TABS: { key: NotificationsTab; title: string }[] = [
  { key: 'platform', title: 'Platform' },
  { key: 'deals', title: 'Deals' },
  { key: 'contacts', title: 'Contacts' },
  { key: 'campaigns', title: 'Campaigns' },
  { key: 'rebates', title: 'Rebates' },
]

// Limited (broker) users only have access to product-related notifications;
// the backend filters the preference rows accordingly (see
// server/notifications/constants.py LIMITED_USER_VISIBLE_NOTIFICATION_SLUGS).
const LIMITED_USER_TABS = ALL_TABS.filter((t) => t.key === 'platform')

export function NotificationsSettingsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { isLimited } = useAuth()
  const tab = (params.tab as NotificationsTab) ?? DEFAULT_TAB

  const tabs = isLimited ? LIMITED_USER_TABS : ALL_TABS
  const validTabs = new Set(tabs.map((t) => t.key))

  if (params.tab && !validTabs.has(params.tab as NotificationsTab)) {
    return <Navigate to={`/settings/notifications/${DEFAULT_TAB}`} replace />
  }

  return (
    <div className="w-full h-full flex flex-col flex-1 min-h-0 bg-[#fefefe]">
      <FBPageInnerTabsContainer>
        <UnderlineTabs<NotificationsTab>
          tabs={tabs}
          activeKey={tab}
          onChange={(value) => navigate(`/settings/notifications/${value}`)}
          idPrefix="notifications-settings"
          aria-label="Notifications settings tabs"
        />
      </FBPageInnerTabsContainer>
      <FBPageTableContainer className="bg-[#fefefe]">
        <NotificationPreferencesTable tab={tab} />
      </FBPageTableContainer>
    </div>
  )
}
